/**
 * BOOT — warm-relaunch tiering (spec §3).
 *
 * The launch celebration is a reward, not a toll: tier the boot beat by how
 * long the app has been backgrounded since it was last in the foreground.
 * The timestamp is persisted in Capacitor Preferences (native UserDefaults →
 * survives a process kill, unlike WebView localStorage under storage
 * pressure). Read once at boot; written on background + once after routing.
 *
 * Thresholds are PROPOSALS from the ratified CD spec — the founder ratifies
 * the final numbers on-device (they stay named here so a tune is one edit).
 */
import { Preferences } from '@capacitor/preferences';

export type BootTier = 'HOT' | 'WARM' | 'COLD';

export const BOOT_LAST_ACTIVE_KEY = 'tayro.bootLastActiveAt';

/** < 60 s since last foreground → HOT (cream pass-through, no beat). */
export const HOT_MAX_MS = 60_000;
/** 60 s – 6 h → WARM (the reprise). ≥ 6 h or first run → COLD (full run). */
export const WARM_MAX_MS = 6 * 60 * 60 * 1000;

/**
 * Pure tier decision from the elapsed gap (ms) since last foreground.
 * `null`/non-finite ⇒ COLD — the SAFE default: a fuller beat, never a wrong
 * short-circuit past the celebration. Exported for tests.
 */
export function tierFor(deltaMs: number | null): BootTier {
  if (deltaMs === null || !Number.isFinite(deltaMs)) return 'COLD';
  if (deltaMs < HOT_MAX_MS) return 'HOT';
  if (deltaMs < WARM_MAX_MS) return 'WARM';
  return 'COLD';
}

/** Read the persisted last-foreground stamp and resolve the boot tier. */
export async function readBootTier(now: number = Date.now()): Promise<BootTier> {
  try {
    const { value } = await Preferences.get({ key: BOOT_LAST_ACTIVE_KEY });
    if (!value) return 'COLD';
    const last = Number(value);
    if (!Number.isFinite(last)) return 'COLD';
    return tierFor(now - last);
  } catch {
    // Preferences unavailable (web / plugin error) — COLD is safe.
    return 'COLD';
  }
}

/** Stamp "the app was in the foreground now". Best-effort — a failed write
 *  just means the next launch reads COLD (the safe, fuller beat). */
export async function markBootActive(now: number = Date.now()): Promise<void> {
  try {
    await Preferences.set({ key: BOOT_LAST_ACTIVE_KEY, value: String(now) });
  } catch {
    /* no-op */
  }
}
